/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * MappingRow (#851) — the shared row for the "independent elements" mapping
 * editors (Request Parameters, Headers, Response Mapping). One consistent, tight
 * treatment so they can't drift apart and don't read as a chunky table:
 *  - rows live inside a single bordered container ({@link MappingRowList}),
 *    separated by hairline dividers rather than a bordered card each;
 *  - a compact single-line header: name/target input · mono value summary · actions;
 *  - **Edit** opens the row; while editing, **Cancel / Apply**
 *    ({@link RowEditActions}) sit in that same top-right spot, beside the row's one
 *    **layer action** ({@link RowLayerAction}) — `✕` only where removing means removing;
 *  - the expanded editor (a `MappingControl` + any per-row extras) renders below.
 *
 * Deliberately distinct from the ORTB body editor, which is a guided-JSON *tree*,
 * not a flat list of independent elements (guidance §17/§18).
 */

import React from 'react';
import { Box, Button, HStack, Text, VisuallyHidden } from '@chakra-ui/react';
import { AuthoredRow, AuthoredRowList, SEVERITY_TONE } from './AuthoredRow';
import type { RowMappingKind, ValueOrigin } from './parameter-editor.adapters';
import { findingLabel } from '@finding-kind';
import { ISSUE_INK, type RowSeverity } from './mapping-issues';
import type { RequiredMark } from './mapping-value-round-trip';
import { ProvenanceMark } from './ProvenanceMark';
import { RowNestedFindingsContext } from './row-nested-findings';
import type { RowLayerActionKind } from '../../../utils/artifact-inheritance';

/**
 * **What the row's one action does to the layer it sits in** (`#1126`, `X31`).
 *
 * Both kinds render as the `✕` — removing is what happens in both — and differ in what they say they
 * remove: the row itself, or the **parent's** row from this source, so the value sent automatically
 * goes out instead. `rowLayerActionKind` decides which; a parent's row that displaces nothing gets no
 * action at all (`null`), because editing and revert already cover it.
 */
export interface RowLayerAction {
  kind: RowLayerActionKind;
  onAct: () => void;
  /** What the row is, for the control's accessible name — `parameter pid`, `header X-Api-Key`. */
  subject: string;
}

function layerActionText(action: RowLayerAction): { label: string; title: string | undefined } {
  return action.kind === 'remove'
    ? { label: `Remove ${action.subject}`, title: undefined }
    : {
        label: `Stop overriding ${action.subject}`,
        title:
          "Stop using the parent's row in this source. What the system sends automatically goes out instead; " +
          'the parent keeps its row, and revert under Sent automatically brings it back here.',
      };
}

/** A row's authoring origin and the way back from a detected guess (`#1001`). See `originMarks`. */
export interface MappingRowProvenance {
  origin: ValueOrigin | undefined;
  onRevert: (() => void) | null;
  /** The way back to the template's current row, for a row changed from one (`TemplateRevert`). */
  templateRevert: TemplateRevert | null;
}

/**
 * **Put back what the template has for this row now** (Todd, 2026-09-24). Offered on a row marked
 * `overridden` that came from a template the source was copied from, where that template still has
 * the row and the viewer can see it.
 */
export interface TemplateRevert {
  templateName: string;
  onRevert: () => void;
}
import { ControlHover } from '../../ui/ControlHover';

/** Tint palette for the value chip, by mapping kind — echoes the body editor's leaf chips. */
const CHIP: Record<RowMappingKind, { bg: string; fg: string }> = {
  static: { bg: 'bg.subtle', fg: 'fg.muted' },
  macro: { bg: 'info.tint', fg: 'info.ink' },
  path: { bg: 'accent.subtle', fg: 'accent.ink' },
  expression: { bg: 'accent.subtle', fg: 'accent.ink' },
  empty: { bg: 'warning.tint', fg: 'warning.ink' },
};

/**
 * The bordered container the rows sit in — hairline dividers, no per-row card.
 * Optional `nameHeader`/`valueHeader` render an aligned column header inside the
 * box (same column widths as {@link MappingRow}) — the response mapper uses it to
 * label the two sides ("Session Context" ← "Response Field") without the row UI
 * having to special-case direction.
 */
export const MappingRowList: React.FC<{
  children: React.ReactNode;
  nameHeader?: string;
  valueHeader?: string;
  /**
   * **A claim true of every row's destination, stated once** (`X32` Q2, `#1248`).
   *
   * A store surface's destination namespace has a lifetime, and on the response surface it is the
   * same one for all twenty-four targets. *One mark per claim, at the level the claim is made* —
   * Todd's rule for Q1's collapsed roots — makes that a property of the **column**, not of each row:
   * repeating an identical sentence twenty-four times teaches nothing and buries the mark that does
   * vary. A path whose lifetime differs from its root's is a different claim and is marked on its
   * own row.
   */
  nameCaveat?: string;
  /**
   * **The same claim in full, for the audience the short form loses** (`#1276`'s rule, 2026-09-10).
   *
   * {@link nameCaveat} is a scope — *Mapped for each ad break* — because a heading has one line to
   * spend. The sentence it shortens is not deleted with it: it hangs here, on a hover that also
   * renders it into the accessible tree, so a keyboard or screen-reader user reads what a mouse
   * user reads. One claim, two audiences — never a second claim.
   */
  nameCaveatDetail?: string;
}> = ({ children, nameHeader, valueHeader, nameCaveat, nameCaveatDetail }) => (
  <AuthoredRowList>
    {(nameHeader || valueHeader) && (
      <HStack
        gap={3}
        align="center"
        px={2}
        py={1.5}
        bg="bg.subtle"
        borderBottomWidth="1px"
        borderColor="border.default"
      >
        <Box minW="150px" maxW="240px" flexShrink={0}>
          <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" color="fg.muted">
            {nameHeader}
          </Text>
          {nameCaveat ? (
            <ControlHover text={nameCaveatDetail ?? null}>
              <Text fontSize="10px" color="fg.subtle" lineHeight="1.3" textTransform="none" letterSpacing="normal">
                {nameCaveat}
              </Text>
            </ControlHover>
          ) : null}
        </Box>
        <Box flex="1" minW="120px">
          <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" color="fg.muted">
            {valueHeader}
          </Text>
        </Box>
      </HStack>
    )}
    {children}
  </AuthoredRowList>
);

export interface MappingRowProps {
  /** The row's name / key / target (left), drawn as text whenever the row is not open. */
  name: string;
  /** A fuller statement of the name, for its hover — the ORTB body's *sent as `plcmt` on 2.6*. */
  nameTitle?: string;
  /**
   * **What kind of target the name is, read out beneath it** (`RQ4`, `RV2`): a round's change row
   * names a floor, a field, a parameter or a header, and the kind is read from the target rather than
   * picked. Optional, because absence is the right answer on every surface whose rows are one kind.
   */
  nameDetail?: string;
  /**
   * **The name's input, shown only while the row is open** (Todd, 2026-09-28: *"I don't think we should
   * allow the user to edit the field names directly (they have to click edit)"*). A name typed into a
   * row nobody opened had no Cancel — Escape kept it — because the row's snapshot is taken when its
   * editor opens. Drawn here, once, so no surface can put a live name box on a closed row again.
   *
   * Required and nullable (`#1134`): `null` is a surface whose rows cannot be renamed here.
   */
  nameInput: React.ReactNode | null;
  /** Mono one-line summary of the current mapping (middle). */
  summaryLabel: string;
  summaryKind: RowMappingKind;
  editing: boolean;
  onEdit: () => void;
  onApply: () => void;
  onCancel: () => void;
  /**
   * The row's layer action — **required and nullable**: `null` is a parent's row with no action of its
   * own to offer, where editing it and the inheritance mark's revert already say everything.
   */
  layerAction: RowLayerAction | null;
  editLabel?: string;
  /**
   * The row's outermost element, for {@link useRevealOnOpen} (guidance §22).
   *
   * A prop rather than `forwardRef` because only *one* row in a list ever wants it —
   * the one an action just opened — and a forwarded ref would have to be threaded
   * through every row to reach it. The editors pass it conditionally.
   */
  rootRef?: React.Ref<HTMLDivElement>;
  /**
   * **The row's DOM id, so it can be revealed without being opened** (`#1305` · `rowAnchorId`).
   *
   * A ref reaches a row this component's parent is already changing; *Review* scrolls to a row and
   * changes nothing, so there is nothing to hang a ref on. Same split as `useRevealOnOpen` and
   * `useRevealAnchor` one layer up.
   */
  rootId?: string;
  /**
   * **`flow`** drops the hairline divider and rounds the hover — the guided tree's
   * treatment (#989). §19's only deliberate difference between the two editor shapes is
   * **separation**: a document's rows flow together, a list's are hairline-separated. That
   * is a property of the *container*, and this prop is what lets the tree keep it while
   * using the same row.
   *
   * §19 already said the list row *"deliberately matches the body row's internals"*. It
   * did not, and could not stay matching while they were two implementations — the columns
   * drifted into three different horizontal positions for the same information. Now there
   * is one row and the claim is structural.
   */
  variant?: 'list' | 'flow';
  /** Highlight the row (e.g. added/updated by a bulk change). */
  highlighted?: boolean;
  /**
   * The row's marks (R4d). A typed set rather than one `badge` slot, because four
   * marks each reaching for a single slot is a collision whoever ships first wins —
   * and they are three different kinds of thing, so the row places them, not the
   * caller.
   *
   * Under width pressure the **value yields and the marks hold**: every mark is
   * unshrinkable and the summary truncates. Dropping a mark would remove
   * information silently exactly when the screen is small, and an operator who
   * learns "no chip means operator-set" would then read that on a laptop.
   */
  marks?: {
    /**
     * Where the value came from. Quiet chip; absent means operator-set.
     *
     * **With the way back, where a detector filled the row** (`#1001`). The pair travels together so a
     * surface passing an origin has to answer `onRevert` — `null` where it holds no detected mappings —
     * which is the required-and-nullable rule applied inside a mark set: an optional revert beside a
     * required origin would compile on a surface that forgot it, and render nothing.
     */
    provenance?: MappingRowProvenance;
    /**
     * What it will resolve to (R4a). Renders as a continuation of the summary.
     *
     * `stale` is not cosmetic: a preview showing a value the current config would no
     * longer produce is worse than no preview, so a stale resolution renders as
     * pending and **never as a value** (R4e).
     *
     * `unresolved` means the value resolved to nothing and the field will be
     * omitted — the #880 semantics, and the authoring signal that change removed.
     */
    resolution?:
      | { value?: string; empty?: boolean; unresolved?: boolean; stale?: boolean }
      /**
       * **A surface that states its own outcome** (`#1144`). The four flags above carry the outbound
       * words — *omitted*, *(empty)*, *what this will send* — which are false on a surface where
       * nothing is sent. The response editor passes the sentence instead; `detail` is its per-ad
       * account, on the hover and in the accessible tree.
       */
      | { statement: string; detail: string; quiet: boolean };
    /** A default standing behind the mapping (#913). */
    default?: string;
    /**
     * Where this row's value came from, when the artifact has a parent (`P3a`) — rendered by
     * `InheritanceMark`, which carries the **revert** control with it.
     *
     * A node rather than a state, because it replaces {@link provenance} rather than sitting beside
     * it: inheritance wins where it has an opinion, and two chips saying different things about one
     * value is what a single vocabulary exists to prevent. It was a bare chip here, which is how the
     * row came to state *"overridden here"* with no way back while the section header two lines up
     * offered one.
     */
    inheritance?: React.ReactNode;
    /**
     * **Legality / redundancy / advisability** (#914, #921, #924) — the leading dot.
     *
     * **Required and nullable**, since 2026-09-22. It was `severity?:` from guidance §16 onward and
     * **not one of the five surfaces supplied it**, so a mark documented below as *"the only mark
     * that says something is wrong"* was rendered by nothing for as long as it existed. That is
     * *an optional prop through a shared component renders nothing*, arriving at a mark instead of
     * a control, and it is invisible in the worst direction: a missing severity mark reads as *no
     * problems*.
     *
     * `null` is a surface saying it has no opinion — a surface that runs no validator, and
     * says so rather than being unable to say anything.
     */
    severity: RowSeverity | null;
    /**
     * **This row's fixed value is a credential** (`#1371`). The row says *secret* and shows *set* in
     * place of the value — the stored one is the placeholder, and a typed one is not for display.
     *
     * **Required and nullable**, for the reason `severity` is: `null` is a surface that stores no
     * credential (a response mapping, a round override), and an optional mark forgotten by a surface
     * that does would draw the placeholder, or the credential, in the collapsed row.
     */
    secret: boolean | null;
    /**
     * **Where this value can be used** — the grain marks a store destination carries (`X32`,
     * `#1248`). Its own slot rather than {@link state}'s, because they are different kinds of thing
     * and the response surface already fills `state` with *added* / *updated*: two claims reaching
     * for one slot is a collision whoever ships first wins, which is the reason `marks` is a typed
     * set at all.
     *
     * It is a **property of the destination**, so it never means the row is wrong and carries no
     * severity — an operator choosing `ad.mediaUrl` deliberately should read it as a fact, not as a
     * warning.
     */
    grain?: { label: string; detail?: string };
    /**
     * **Where else this request sends the same value** (`#944`) — `in 3 places`, the others named.
     *
     * Its own slot for the reason `grain` has one. No severity: repeating a signal across `device`
     * and `imp` is correct and usual; the mark is there so that changing one occurrence is not done
     * without knowing about the rest. The list is in the accessible tree, not only the hover.
     */
    occurrences?: { label: string; detail: string };
    /**
     * **This row sends a different value from another that carries the same concept** (`#944`) —
     * *conflicts with body device.ua*. Warning-toned, where `occurrences` is quiet: a request that
     * disagrees with itself is the defect `#927` is, and the one mark here that says something may be
     * wrong. Never an error — two values can be meant.
     */
    conflict?: { label: string; detail: string };
    /**
     * **How many times this row runs, where it is more than once** — cardinality (`X56a`, `#1296`).
     *
     * Its own slot rather than {@link grain}'s, for the reason `grain` has its own: that one is a
     * property of the **destination** (*where can this be used*), this one of the **source** (*is
     * this one value or one per ad*), and two claims reaching for one slot is a collision whoever
     * ships first wins.
     *
     * Like `grain` it carries no severity — reading a value that is the same for every ad is what
     * `demandSource.*` is offered here for, and a surface that warns about correct behaviour trains
     * the operator to ignore the warnings that matter.
     */
    cardinality?: { label: string; detail?: string };
    /**
     * **The shared expression this row addresses has moved since the row recorded it** (`#1302`).
     *
     * Its own slot for the reason the set is typed at all: it is a different kind of claim from
     * every other mark here. The rest state a property of the value or its destination and carry no
     * severity; this one asks the author to **look**, and it is a warning rather than an error
     * because nothing is wrong — the new expression may be exactly what the row wants.
     *
     * Derived by `leafAddressesChangedExpression`, never per editor: five surfaces build this mark
     * set, and a reader narrower than the writer produces **no mark**, which is indistinguishable
     * from nothing having changed.
     */
    sharedChanged?: boolean;
    /** One-off state marker — the former `badge` slot ("ADDED", "SUPPRESSED"). */
    state?: React.ReactNode;
  };
  /**
   * Read-only render (detail-page preview): name + value pill only — no
   * Edit/✕/Apply actions, no expansion, tighter vertical rhythm. Keeps the row
   * treatment identical to the editor so preview and edit read as one thing.
   */
  readOnly?: boolean;
  /** The expanded editor — rendered below the header only while `editing`. */
  children?: React.ReactNode;
}

/**
 * **The required mark, rendered once for every surface** (`N11g`; extended to the ORTB body leaf
 * 2026-08-27).
 *
 * It sits in the row's `state` slot, which is the same slot `SUPPRESSED` uses — *never send*
 * against *always expected* in one place, so a contradictory pair cannot be rendered.
 *
 * The projection is {@link describeRequiredClaim}; this is only its appearance. They were one
 * inline block in `RequestParameterEditor` before the body leaf needed it, which is how the
 * *second* surface's copy comes to say something slightly different from the first's.
 */
/**
 * **What a row's `state` slot says — one rendering for every surface** (found 2026-09-23, `#1421`).
 *
 * *Never send it* and the required claim share one slot, so the contradictory pair is unrenderable.
 * Parameters rendered both, the ORTB body only the required mark, and headers neither — so a header or
 * a body leaf set to *Never send it* looked exactly like one that is sent. Each surface had written
 * its own `state` expression, and each wrote the part it was looking at. Rendered here, a surface
 * passes the two facts and cannot drop one.
 */
export const RowStateMark: React.FC<{ suppressed: boolean; required: RequiredMark | null }> = ({
  suppressed,
  required,
}) =>
  suppressed ? (
    <Text fontSize="10px" fontWeight="700" color="error.solid" flexShrink={0}>
      SUPPRESSED
    </Text>
  ) : (
    <RequiredMarkText mark={required} />
  );

export const RequiredMarkText: React.FC<{ mark: RequiredMark | null }> = ({ mark }) =>
  mark ? (
    <Text
      fontSize="10px"
      fontWeight={mark.tone === 'operator' ? '600' : '700'}
      color={mark.tone === 'operator' ? 'accent.solid' : 'fg.subtle'}
      flexShrink={0}
      title={mark.title}
    >
      {mark.label}
    </Text>
  ) : null;

export const MappingRow: React.FC<MappingRowProps> = ({
  name,
  nameTitle,
  nameDetail,
  nameInput,
  summaryLabel,
  summaryKind,
  editing,
  onEdit,
  onApply,
  onCancel,
  layerAction,
  editLabel = 'Edit mapping',
  rootRef,
  rootId,
  variant = 'list',
  highlighted,
  marks,
  readOnly,
  children,
}) => {
  // A surface either states its own outcome or hands over the outbound flags (`#1144`).
  const resolution = marks?.resolution;
  // A credential's value is never drawn: the stored one is the placeholder, a typed one is the secret.
  const secretValue = marks?.secret === true && summaryKind === 'static';
  const shownLabel = secretValue ? 'set' : summaryLabel;
  const statedResolution = resolution && 'statement' in resolution ? resolution : null;
  const severity = marks?.severity ?? null;
  const flaggedResolution = resolution && !('statement' in resolution) ? resolution : null;
  return (
    <AuthoredRow
      rootRef={rootRef}
      rootId={rootId}
      variant={variant}
      highlighted={highlighted}
      readOnly={Boolean(readOnly)}
      // A mapping row's summary already states its whole value, so read-only it does not open.
      readView={null}
      onReadingChange={null}
      editing={editing}
      onEdit={onEdit}
      onApply={onApply}
      onCancel={onCancel}
      editLabel={editLabel}
      severity={severity}
      head={
        editing && !readOnly && nameInput ? (
          nameInput
        ) : (
          <>
            <Text fontSize="sm" fontFamily="mono" color="fg.default" truncate title={nameTitle ?? name}>
              {name || '—'}
            </Text>
            {nameDetail ? (
              <Text fontSize="10px" color="fg.subtle" truncate>
                {nameDetail}
              </Text>
            ) : null}
          </>
        )
      }
      summary={
        <>
          {/* Compact pill — echoes the ORTB body editor's leaf chip. **Its fill is the value's kind and
          its outline is the row's severity** (`#1412`, Todd 2026-09-22): one colour meaning two
          things would be one glyph with two names (`#1269`), so a finding never changes the fill. */}
          <Box
            display="inline-flex"
            alignItems="baseline"
            gap={1.5}
            maxW="100%"
            bg={CHIP[summaryKind].bg}
            color={CHIP[summaryKind].fg}
            borderRadius="full"
            px={2}
            py={0.5}
            outline={severity ? '1.5px solid' : undefined}
            outlineColor={severity ? SEVERITY_TONE[severity.level] : undefined}
            // **Drawn inside the pill** — the value's column clips (`overflow: hidden`, for the
            // truncation), and an outline drawn outside was cut off top and bottom (Todd, 2026-09-28).
            outlineOffset={severity ? '-1.5px' : undefined}
            data-pill-severity={severity?.level}
          >
            <Text
              fontSize="xs"
              fontFamily="mono"
              fontStyle={summaryKind === 'empty' ? 'italic' : undefined}
              truncate
              minW={0}
              title={shownLabel}
            >
              {shownLabel}
              {/* Resolution continues the value rather than sitting beside it (R4d):
              the operator is looking at one thing — what this row sends. An
              unbounded resolved string in the mark strip pushes the actions off
              the row at the first long user-agent. */}
              {statedResolution ? (
                <Text
                  as="span"
                  color={statedResolution.quiet ? 'fg.subtle' : 'fg.muted'}
                  title={statedResolution.detail}
                >
                  {statedResolution.statement}
                  <VisuallyHidden>{`. ${statedResolution.detail}`}</VisuallyHidden>
                </Text>
              ) : flaggedResolution ? (
                <Text
                  as="span"
                  color={
                    flaggedResolution.stale || flaggedResolution.unresolved || flaggedResolution.empty
                      ? 'fg.subtle'
                      : 'fg.muted'
                  }
                  fontStyle={flaggedResolution.stale ? 'italic' : undefined}
                  title={
                    flaggedResolution.stale
                      ? 'Resolving — apply your change to see what this will send'
                      : flaggedResolution.unresolved
                        ? 'Resolves to nothing — this field will be omitted'
                        : flaggedResolution.empty
                          ? 'Resolves to an empty value — the field is sent, with nothing in it'
                          : undefined
                  }
                >
                  {flaggedResolution.stale
                    ? ' → …'
                    : flaggedResolution.unresolved
                      ? ' → omitted'
                      : flaggedResolution.empty
                        ? ' → (empty)'
                        : ` → ${flaggedResolution.value ?? ''}`}
                </Text>
              ) : null}
            </Text>
            {/* **What kind of thing is wrong, in two words** (`3i`) — *out of range*, *wrong type* —
            from the one closed vocabulary every validator reports with. Not the finding's
            sentence: that is the dot's accessible name and the banner's line. Its own span, so a
            long value truncates and this does not: it is the part of the row that says
            something is wrong, and a mark dropped under width pressure reads as *fine*. */}
            {severity ? (
              <Text
                as="span"
                flexShrink={0}
                fontSize="xs"
                fontWeight="600"
                color={ISSUE_INK[severity.level]}
                whiteSpace="nowrap"
                data-finding-kind={severity.kind}
              >
                {findingLabel(severity.kind)}
              </Text>
            ) : null}
          </Box>
        </>
      }
      marks={
        <>
          {marks?.secret ? (
            <Box
              as="span"
              flexShrink={0}
              fontSize="10px"
              px={1.5}
              borderRadius="sm"
              bg="bg.muted"
              color="fg.muted"
              whiteSpace="nowrap"
              title="A credential: its fixed value is stored encrypted and is not shown."
            >
              secret
            </Box>
          ) : null}
          {/* Quiet marks, in sacrifice order: the default drops before provenance. */}
          {marks?.default ? (
            <Box
              as="span"
              flexShrink={0}
              fontSize="10px"
              fontFamily="mono"
              color="fg.subtle"
              whiteSpace="nowrap"
              title={`Defaults to ${marks.default}`}
            >
              {`⤵ ${marks.default}`}
            </Box>
          ) : null}
          {marks?.sharedChanged ? (
            /* Louder than the quiet marks above it, because it is the one that asks for an action —
           and worded as an observation, never as a verdict on the value (#1045). */
            <Box
              as="span"
              flexShrink={0}
              fontSize="10px"
              px={1.5}
              borderRadius="sm"
              bg="warning.tint"
              color="warning.ink"
              whiteSpace="nowrap"
              title="This shared expression has been edited since this row recorded it. Open the row to see what it does now."
            >
              changed
            </Box>
          ) : null}
          {marks?.inheritance ??
            (marks?.provenance ? (
              <ProvenanceMark
                origin={marks.provenance.origin}
                onRevert={marks.provenance.onRevert}
                templateRevert={marks.provenance.templateRevert}
              />
            ) : null)}
          {marks?.grain ? (
            <Text fontSize="10px" color="fg.subtle" flexShrink={0} whiteSpace="nowrap" title={marks.grain.detail}>
              {marks.grain.label}
            </Text>
          ) : null}
          {marks?.occurrences ? (
            <Text
              fontSize="10px"
              color="fg.subtle"
              flexShrink={0}
              whiteSpace="nowrap"
              title={marks.occurrences.detail}
              data-mark="occurrences"
            >
              {marks.occurrences.label}
              <VisuallyHidden>{`. ${marks.occurrences.detail}`}</VisuallyHidden>
            </Text>
          ) : null}
          {marks?.conflict ? (
            <Text
              fontSize="10px"
              fontWeight="600"
              color="warning.ink"
              flexShrink={0}
              whiteSpace="nowrap"
              title={marks.conflict.detail}
              data-mark="conflict"
            >
              {marks.conflict.label}
              <VisuallyHidden>{`. ${marks.conflict.detail}`}</VisuallyHidden>
            </Text>
          ) : null}
          {marks?.cardinality ? (
            <Text fontSize="10px" color="fg.subtle" flexShrink={0} whiteSpace="nowrap" title={marks.cardinality.detail}>
              {marks.cardinality.label}
            </Text>
          ) : null}
          {marks?.state}
        </>
      }
      actions={
        layerAction ? (
          <Button
            size="xs"
            variant="ghost"
            color="fg.muted"
            onClick={layerAction.onAct}
            title={layerActionText(layerAction).title}
            aria-label={layerActionText(layerAction).label}
          >
            ✕
          </Button>
        ) : null
      }
    >
      <RowNestedFindingsContext.Provider value={severity?.nested ?? null}>{children}</RowNestedFindingsContext.Provider>
    </AuthoredRow>
  );
};
